/**
 * Promise client for the solver worker with latest-wins coalescing: a newer
 * solve supersedes an in-flight one (its response is dropped on arrival —
 * single-threaded WASM cannot be preempted), and restart() hard-kills a stuck
 * worker.
 */
import type { CoaxSpec } from "@kazam/xsec";
import type { SolveOptions, SolveResultMsg, SweepOptions, SweepResultMsg, WorkerRequest, WorkerResponse } from "./protocol";

type Pending<T> = { resolve: (v: T) => void; reject: (e: Error) => void };

export class SolverClient {
  private worker!: Worker;
  /** Separate worker for RLGC sweeps (closed forms, instant) so they never
   * queue behind a long field solve on the single-threaded solve worker. */
  private sweepWorker!: Worker;
  private nextId = 1;
  private readyPromise!: Promise<number>;
  private readyResolve!: (abi: number) => void;
  /** Only the latest request per channel is honored. */
  private pendingSolve: { id: number; p: Pending<SolveResultMsg> } | null = null;
  private pendingSweep: { id: number; p: Pending<SweepResultMsg> } | null = null;

  constructor() {
    this.spawn();
  }

  private spawn(): void {
    this.readyPromise = new Promise((res) => (this.readyResolve = res));
    const mk = () => new Worker(new URL("./solver.worker.ts", import.meta.url), { type: "module" });
    this.worker = mk();
    this.sweepWorker = mk();
    const onMsg = (ev: MessageEvent<WorkerResponse>) => this.onMessage(ev.data);
    this.worker.onmessage = onMsg;
    this.sweepWorker.onmessage = onMsg;
    this.worker.onerror = this.sweepWorker.onerror = (ev) => {
      const err = new Error(ev.message || "solver worker error");
      this.pendingSolve?.p.reject(err);
      this.pendingSweep?.p.reject(err);
      this.pendingSolve = this.pendingSweep = null;
    };
  }

  private onMessage(msg: WorkerResponse): void {
    if (msg.type === "ready") {
      this.readyResolve(msg.abiVersion);
      return;
    }
    if (msg.type === "result") {
      if (this.pendingSolve && msg.id === this.pendingSolve.id) {
        this.pendingSolve.p.resolve(msg);
        this.pendingSolve = null;
      }
      return; // stale result: drop
    }
    if (msg.type === "sweepResult") {
      if (this.pendingSweep && msg.id === this.pendingSweep.id) {
        this.pendingSweep.p.resolve(msg);
        this.pendingSweep = null;
      }
      return;
    }
    if (msg.type === "error") {
      if (this.pendingSolve && msg.id === this.pendingSolve.id) {
        this.pendingSolve.p.reject(new Error(msg.message));
        this.pendingSolve = null;
      } else if (this.pendingSweep && msg.id === this.pendingSweep.id) {
        this.pendingSweep.p.reject(new Error(msg.message));
        this.pendingSweep = null;
      }
    }
  }

  ready(): Promise<number> {
    return this.readyPromise;
  }

  /** Solve; a newer call supersedes an unanswered one (superseded promise rejects). */
  solve(spec: CoaxSpec, options: SolveOptions): Promise<SolveResultMsg> {
    const id = this.nextId++;
    this.pendingSolve?.p.reject(new Error("superseded"));
    return new Promise<SolveResultMsg>((resolve, reject) => {
      this.pendingSolve = { id, p: { resolve, reject } };
      const req: WorkerRequest = { type: "solve", id, spec, options };
      this.worker.postMessage(req);
    });
  }

  sweep(spec: CoaxSpec, options: SweepOptions): Promise<SweepResultMsg> {
    const id = this.nextId++;
    this.pendingSweep?.p.reject(new Error("superseded"));
    return new Promise<SweepResultMsg>((resolve, reject) => {
      this.pendingSweep = { id, p: { resolve, reject } };
      const req: WorkerRequest = { type: "sweep", id, spec, options };
      this.sweepWorker.postMessage(req);
    });
  }

  /** Hard-kill a stuck worker and respawn (in-flight promises reject). */
  restart(): void {
    this.worker.terminate();
    this.sweepWorker.terminate();
    const err = new Error("solver restarted");
    this.pendingSolve?.p.reject(err);
    this.pendingSweep?.p.reject(err);
    this.pendingSolve = this.pendingSweep = null;
    this.spawn();
  }
}

export const solver = new SolverClient();
